import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, X, Loader, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { ImageUpload } from './ImageUpload';
import { GalleryUpload } from './GalleryUpload';
import { ImageGallery } from './ImageGallery';
import { getTranslatedCities, getTranslatedServiceCategories } from '../lib/constants';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import GoogleMapLocationPicker from './GoogleMapLocationPicker.js';
import { RichTextEditor } from './RichTextEditor';

interface ProviderProfileFormProps {
  initialData: any;
  onSubmit: (data: any) => Promise<void>;
  onGalleryUpdate: () => void;
  onCancel?: () => void;
}

interface CategorySelectorProps {
  selectedServices: string[];
  onServicesChange: (services: string[]) => void;
  categoryId: string;
}

const DEFAULT_LOCATION = { lat: 37.7749, lng: -122.4194, address: 'San Francisco, CA' };

const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedServices,
  onServicesChange,
  categoryId
}) => {
  const { t, language } = useLanguage();
  const serviceCategories = getTranslatedServiceCategories(language);

  // Filter services based on the selected category
  const filteredServices = serviceCategories
    .find(cat => cat.categoryId === categoryId)
    ?.services || [];

  const handleServiceSelect = (serviceId: string) => {
    if (selectedServices.includes(serviceId)) {
      onServicesChange(selectedServices.filter(id => id !== serviceId));
    } else {
      onServicesChange([...selectedServices, serviceId]);
    }
  };

  return (
    <div className="mt-2">
      {filteredServices.map(service => (
        <button
          key={service.id}
          type="button"
          className={`w-full flex items-center pl-4 border-b border-gray-200 py-2 text-left ${
            selectedServices.includes(service.id)
              ? 'bg-primary-100 text-primary-800'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
          onClick={() => handleServiceSelect(service.id)}
        >
          {service.name}
        </button>
      ))}
    </div>
  );
};

export const ProviderProfileForm: React.FC<ProviderProfileFormProps> = ({
  initialData,
  onSubmit,
  onGalleryUpdate,
  onCancel
}) => {
  const { t, language } = useLanguage();
  const [showGalleryUpload, setShowGalleryUpload] = useState(false);
  const [galleryImages, setGalleryImages] = useState<any[]>(initialData?.gallery || []);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(initialData?.category || '');
  const [selectedServices, setSelectedServices] = useState<string[]>(initialData?.services || []);
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    initialData?.service_locations || []
  );
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>(
    initialData?.availability || []
  );
  const [isServicesExpanded, setIsServicesExpanded] = useState(false);

  // NEW: Keep track of location dropdown expansion for consistency
  const [isLocationsExpanded, setIsLocationsExpanded] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
    address: string;
  } | null>(initialData?.map_location || DEFAULT_LOCATION);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...initialData,
      business_name: initialData?.business_name || '',
      image_url: initialData?.image_url || '',
      short_description: initialData?.short_description || '',
      service_details: initialData?.service_details || '',
      whatsapp: initialData?.whatsapp || '',
      telegram: initialData?.telegram || '',
      messenger: initialData?.messenger || '',
      phone: initialData?.phone || '',
    },
  });

  const imageUrl = watch('image_url');
  const cities = getTranslatedCities(language);
  const serviceCategories = getTranslatedServiceCategories(language);

  useEffect(() => {
    if (initialData?.map_location?.lat && initialData.map_location?.lng) {
      setSelectedLocation(initialData.map_location);
    } else {
      setSelectedLocation(DEFAULT_LOCATION);
    }
  }, [initialData]);

  const availableServices = selectedCategory
  ? serviceCategories.find((cat) => cat.categoryId === selectedCategory)?.services || []
  : [];

  const handleLocationSelect = (location: any) => {
    const formattedAddress = location.businessName
      ? `${location.businessName}, ${location.address}`
      : location.address;
    setSelectedLocation({ lat: location.lat, lng: location.lng, address: formattedAddress });
    setValue('map_location', { lat: location.lat, lng: location.lng, address: formattedAddress });
  };

  const toggleLocation = (cityId: string) => {
    setSelectedLocations(prev =>
      prev.includes(cityId) ? prev.filter(id => id !== cityId) : [...prev, cityId]
    );
  };

  const availabilityPeriods = [
    { id: 'weekdays', label: t('weekdays') },
    { id: 'weekends', label: t('weekends') },
    { id: 'evenings', label: t('evenings') },
    { id: 'emergency', label: t('emergency') }
  ];

  const toggleAvailability = (periodId: string) => {
    setSelectedAvailability(prev =>
      prev.includes(periodId) ? prev.filter(id => id !== periodId) : [...prev, periodId]
    );
  };

  const handleFormSubmit = async (data: any) => {
    try {
      setSubmitting(true);
      const { gallery, ...formData } = data;

      const profileData = {
        ...formData,
        category: selectedCategory,
        services: selectedServices,
        service_locations: selectedLocations,
        availability: selectedAvailability,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        experience: parseInt(formData.experience, 10),
        updated_at: new Date().toISOString(),
        map_location: selectedLocation,
      };

      await onSubmit(profileData);
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error('Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = (url: string) => {
    setValue('image_url', url);
  };

  const toggleServicesExpansion = () => {
    setIsServicesExpanded(!isServicesExpanded);
  };

  return (
    <div className="relative h-full">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('editProviderProfile')}</h2>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={t('close')}
        >
          <X className="w-6 h-6 text-gray-500" />
        </button>
      </div>

      {/* Main Form */}
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8 overflow-y-auto h-full p-4">
        {/* Profile Image */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('profileImage')}</h3>
          <ImageUpload onUpload={handleImageUpload} currentImage={imageUrl} />
        </div>

        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('basicInfo')}</h3>

          {/* Business Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('businessName')}
            </label>
            <input
              type="text"
              {...register('business_name', { required: t('required') })}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
            />
            {errors.business_name && (
              <p className="mt-2 text-sm text-red-600">{errors.business_name.message}</p>
            )}
          </div>

          {/* Location (City) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('location')}
            </label>
            <select
              {...register('location', { required: t('required') })}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
            >
              <option value="">{t('selectCity')}</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
            {errors.location && (
              <p className="mt-2 text-sm text-red-600">{errors.location.message}</p>
            )}
          </div>

          {/* Google Map Picker (no extra container) */}
          <div className="mb-6">
            <h4 className="text-lg font-medium text-gray-800 mb-2">{t('buisnessLocation')}</h4>
            <GoogleMapLocationPicker onLocationSelect={handleLocationSelect} />
          </div>

          {/* Service Locations (multi-select dropdown style) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('serviceLocations')}
          </label>

          {/* Display the selected locations as chips (or a summary) */}
          <div className="mb-2">
            <span className="text-gray-700 text-sm font-semibold mr-1">{t('selected')}:</span>
            {selectedLocations.length > 0 ? (
              selectedLocations.map((cityId) => {
                const city = cities.find((c) => c.id === cityId);
                return (
                  <span
                    key={cityId}
                    className="inline-block px-2 py-1 bg-primary-100 text-blue-800 rounded-full text-xs mr-2"
                  >
                    {city ? city.name : cityId}
                  </span>
                );
              })
            ) : (
              <span className="text-gray-500 text-sm italic">{t('noneSelected')}</span>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsLocationsExpanded(!isLocationsExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          >
            <span>{t('selectServiceLocations')}</span>
            {isLocationsExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          {isLocationsExpanded && (
            <div className="mt-2">
              {cities.map((city) => (
                <button
                  key={city.id}
                  type="button"
                  className={`w-full flex items-center pl-4 border-b border-gray-200 py-2 text-left ${
                    selectedLocations.includes(city.id)
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => toggleLocation(city.id)}
                >
                  {city.name}
                </button>
              ))}
            </div>
          )}
        </div>


          {/* Short Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('shortDescription')}
            </label>
            <input
              type="text"
              {...register('short_description', {
                required: t('required'),
                maxLength: {
                  value: 50,
                  message: t('shortDescriptionMaxLength'),
                },
              })}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              placeholder={t('shortDescriptionPlaceholder')}
            />
            {errors.short_description && (
              <p className="mt-2 text-sm text-red-600">{errors.short_description.message}</p>
            )}
          </div>

          {/* Service Details (Using RichTextEditor) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('serviceDetails')}
            </label>
            <div className="border border-gray-200 rounded-lg">
              <RichTextEditor
                initialContent={initialData?.service_details || ''}
                onChange={(html) => setValue('service_details', html)}
              />
            </div>
          </div>

          {/* Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('serviceCategory')}
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
            >
              <option value="">{t('selectServiceCategory')}</option>
              {serviceCategories.map((category) => (
                <option key={category.categoryId} value={category.categoryId}>
                  {category.categoryName}
                </option>
              ))}
            </select>
          </div>

                {/* Services Offered */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('servicesOffered')}
          </label>

          {/* Display the selected services as chips (or a summary) */}
          <div className="mb-2">
            <span className="text-gray-700 text-sm font-semibold mr-1">{t('selected')}:</span>
            {selectedServices.length > 0 ? (
              selectedServices.map((serviceId) => {
                const service = availableServices.find((s) => s.id === serviceId);
                return (
                  <span
                    key={serviceId}
                    className="inline-block px-2 py-1 bg-primary-100 text-blue-800 rounded-full text-xs mr-2"
                  >
                    {service ? service.name : serviceId}
                  </span>
                );
              })
            ) : (
              <span className="text-gray-500 text-sm italic">{t('noneSelected')}</span>
            )}
          </div>

          <button
            type="button"
            onClick={toggleServicesExpansion}
            className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          >
            <span>{t('servicesOffered')}</span>
            {isServicesExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          {isServicesExpanded && selectedCategory && (
            <CategorySelector
              selectedServices={selectedServices}
              onServicesChange={setSelectedServices}
              categoryId={selectedCategory}
            />
          )}
        </div>


          {/* Experience & Hourly Rate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('yearsExperience')}
              </label>
              <input
                type="number"
                {...register('experience', {
                  min: { value: 0, message: t('experienceMin') },
                })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              />
              {errors.experience && (
                <p className="mt-2 text-sm text-red-600">{errors.experience.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('hourlyRate')}
              </label>
              <input
                type="number"
                step="0.01"
                {...register('hourly_rate')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                placeholder={t('optional')}
              />
            </div>
          </div>

          {/* Availability */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('availability')}
            </label>
            <div className="flex flex-wrap gap-2">
              {availabilityPeriods.map(period => {
                const isSelected = selectedAvailability.includes(period.id);
                return (
                  <button
                    key={period.id}
                    type="button"
                    onClick={() => toggleAvailability(period.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {period.label}
                    {isSelected && <X className="w-4 h-4 ml-1 inline-block" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('contactInfo')}</h3>
          <div className="space-y-6">
            {/* WhatsApp */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('whatsappNumber')}
              </label>
              <input
                type="text"
                {...register('whatsapp')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                placeholder={t('whatsappPlaceholder')}
              />
            </div>
            {/* Telegram */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('telegramUsername')}
              </label>
              <input
                type="text"
                {...register('telegram')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                placeholder={t('telegramPlaceholder')}
              />
            </div>
            {/* Messenger */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('messengerUsername')}
              </label>
              <input
                type="text"
                {...register('messenger')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                placeholder={t('messengerPlaceholder')}
              />
            </div>
            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('phoneNumber')}
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                placeholder={t('phonePlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* Work Gallery */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">{t('workGallery')}</h3>
            <button
              type="button"
              onClick={() => setShowGalleryUpload(!showGalleryUpload)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all"
            >
              {showGalleryUpload ? (
                <>
                  <X className="w-4 h-4" />
                  {t('cancel')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {t('addImage')}
                </>
              )}
            </button>
          </div>

          {showGalleryUpload && (
            <div className="mb-6">
              <GalleryUpload
                providerId={initialData?.user_id}
                onUploadComplete={(url) => {
                  setGalleryImages((prev) => [...prev, { id: uuidv4(), image_url: url, caption: '' }]);
                  setShowGalleryUpload(false);
                  onGalleryUpdate?.();
                }}
              />
            </div>
          )}

          <ImageGallery
            images={galleryImages}
            isOwner={true}
            onDelete={(id) => {
              setGalleryImages((prev) => prev.filter((image) => image.id !== id));
            }}
          />
        </div>

        {/* Submit & Cancel Buttons */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-4 pt-8 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-3 text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin" />
                {t('saving')}
              </span>
            ) : (
              t('saveChanges')
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
